import React, { useState } from 'react';
import {
  View,
  Modal,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useTheme } from '../../theme/ThemeContext';
import ThemedText from '../common/ThemedText';

type ContractStatus = 'completed' | 'partially_completed' | 'cancelled';

interface EndContractModalProps {
  visible: boolean;
  onClose: () => void;
  onSubmit: (statusOrAction: ContractStatus | 'accept' | 'reject') => Promise<void>;
  mode: 'request' | 'respond';
  requestedStatus?: ContractStatus;
  jobTitle: string;
}

const STATUS_OPTIONS: Array<{
  value: ContractStatus;
  label: string;
  description: string;
  icon: 'checkmark-circle' | 'alert-circle' | 'close-circle';
  color: string;
}> = [
  {
    value: 'completed',
    label: 'Completed',
    description: 'Work finished successfully',
    icon: 'checkmark-circle',
    color: '#10B981',
  },
  {
    value: 'partially_completed',
    label: 'Partially Completed',
    description: 'Work partially done',
    icon: 'alert-circle',
    color: '#F59E0B',
  },
  {
    value: 'cancelled',
    label: 'Cancelled',
    description: 'Contract terminated',
    icon: 'close-circle',
    color: '#EF4444',
  },
];

const EndContractModal: React.FC<EndContractModalProps> = ({
  visible,
  onClose,
  onSubmit,
  mode,
  requestedStatus,
  jobTitle,
}) => {
  const { theme, isDark } = useTheme();
  const insets = useSafeAreaInsets();
  const [selectedStatus, setSelectedStatus] = useState<ContractStatus | null>(
    mode === 'respond' && requestedStatus ? requestedStatus : null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (action?: 'accept' | 'reject') => {
    if (mode === 'request' && !selectedStatus) return;
    
    try {
      setIsSubmitting(true);
      
      if (mode === 'respond' && action) {
        // In respond mode, pass the action directly
        await onSubmit(action);
      } else if (mode === 'request' && selectedStatus) {
        // In request mode, submit the selected status
        await onSubmit(selectedStatus);
      }
      
      onClose();
    } catch (error) {
      console.error('Error submitting end contract:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusOption = (status: ContractStatus) => {
    return STATUS_OPTIONS.find((opt) => opt.value === status);
  };

  const requestedOption = requestedStatus ? getStatusOption(requestedStatus) : null;

  return (
    <Modal
      visible={visible}
      animationType="slide"
      transparent
      onRequestClose={onClose}
    >
      <View style={styles.overlay}>
        <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={onClose} />
        
        <View style={[styles.modalContainer, { backgroundColor: theme.card }]}>
          {/* Header */}
          <View style={[styles.header, { borderBottomColor: theme.border }]}>
            <View style={styles.headerLeft}>
              <Ionicons
                name={mode === 'request' ? 'document-text' : 'alert-circle'}
                size={24}
                color={theme.accent}
              />
              <ThemedText style={[styles.headerTitle, { color: theme.text }]}>
                {mode === 'request' ? 'End Contract' : 'Respond to Request'}
              </ThemedText>
            </View>
            <TouchableOpacity onPress={onClose} disabled={isSubmitting}>
              <Ionicons name="close" size={24} color={theme.textSecondary} />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
            {/* Job Title */}
            <View style={[styles.jobCard, { backgroundColor: theme.backgroundSecondary }]}>
              <ThemedText style={[styles.jobLabel, { color: theme.textSecondary }]}>
                Contract
              </ThemedText>
              <ThemedText style={[styles.jobTitle, { color: theme.text }]}>
                {jobTitle}
              </ThemedText>
            </View>

            {mode === 'request' ? (
              // Request Mode - Select Status
              <>
                <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>
                  How would you like to end this contract?
                </ThemedText>
                
                {STATUS_OPTIONS.map((option) => (
                  <TouchableOpacity
                    key={option.value}
                    style={[
                      styles.statusOption,
                      {
                        backgroundColor: theme.background,
                        borderColor:
                          selectedStatus === option.value
                            ? option.color
                            : theme.border,
                        borderWidth: selectedStatus === option.value ? 2 : 1,
                      },
                    ]}
                    onPress={() => setSelectedStatus(option.value)}
                    activeOpacity={0.7}
                    disabled={isSubmitting}
                  >
                    <Ionicons
                      name={option.icon}
                      size={32}
                      color={
                        selectedStatus === option.value
                          ? option.color
                          : theme.textSecondary
                      }
                    />
                    <View style={styles.statusInfo}>
                      <ThemedText
                        style={[
                          styles.statusLabel,
                          {
                            color:
                              selectedStatus === option.value
                                ? theme.text
                                : theme.textSecondary,
                          },
                        ]}
                      >
                        {option.label}
                      </ThemedText>
                      <ThemedText
                        style={[styles.statusDescription, { color: theme.textSecondary }]}
                      >
                        {option.description}
                      </ThemedText>
                    </View>
                    {selectedStatus === option.value && (
                      <Ionicons name="checkmark-circle" size={20} color={option.color} />
                    )}
                  </TouchableOpacity>
                ))}

                <View style={[styles.infoBox, { backgroundColor: isDark ? 'rgba(59, 130, 246, 0.1)' : 'rgba(59, 130, 246, 0.05)' }]}>
                  <Ionicons name="information-circle" size={20} color={theme.primary} />
                  <ThemedText style={[styles.infoText, { color: theme.textSecondary }]}>
                    The other party must accept your request before the contract status changes.
                  </ThemedText>
                </View>
              </>
            ) : (
              // Respond Mode - Accept or Reject
              <>
                <ThemedText style={[styles.sectionTitle, { color: theme.text }]}>
                  The other party requested to end the contract as:
                </ThemedText>

                {requestedOption && (
                  <View
                    style={[
                      styles.requestedStatus,
                      {
                        backgroundColor: theme.background,
                        borderColor: requestedOption.color,
                      },
                    ]}
                  >
                    <Ionicons
                      name={requestedOption.icon}
                      size={40}
                      color={requestedOption.color}
                    />
                    <View style={styles.requestedInfo}>
                      <ThemedText style={[styles.requestedLabel, { color: theme.text }]}>
                        {requestedOption.label}
                      </ThemedText>
                      <ThemedText
                        style={[styles.requestedDescription, { color: theme.textSecondary }]}
                      >
                        {requestedOption.description}
                      </ThemedText>
                    </View>
                  </View>
                )}

                <View style={[styles.infoBox, { backgroundColor: isDark ? 'rgba(245, 158, 11, 0.1)' : 'rgba(245, 158, 11, 0.05)' }]}>
                  <Ionicons name="alert-circle" size={20} color="#F59E0B" />
                  <ThemedText style={[styles.infoText, { color: theme.textSecondary }]}>
                    If you reject, the other party can submit a new request with a different status.
                  </ThemedText>
                </View>
              </>
            )}
          </ScrollView>

          {/* Footer Buttons */}
          <View style={[styles.footer, { borderTopColor: theme.border, paddingBottom: insets.bottom + 16 }]}>
            {mode === 'request' ? (
              <>
                <TouchableOpacity
                  style={[styles.button, styles.cancelButton, { backgroundColor: theme.backgroundSecondary }]}
                  onPress={onClose}
                  disabled={isSubmitting}
                >
                  <ThemedText style={[styles.buttonText, { color: theme.textSecondary }]}>
                    Cancel
                  </ThemedText>
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.button,
                    styles.submitButton,
                    {
                      backgroundColor: selectedStatus ? theme.accent : theme.border,
                      opacity: selectedStatus && !isSubmitting ? 1 : 0.5,
                    },
                  ]}
                  onPress={() => handleSubmit()}
                  disabled={!selectedStatus || isSubmitting}
                >
                  {isSubmitting ? (
                    <ActivityIndicator color="#FFFFFF" size="small" />
                  ) : (
                    <ThemedText style={styles.submitButtonText}>Send Request</ThemedText>
                  )}
                </TouchableOpacity>
              </>
            ) : (
              <>
                <TouchableOpacity
                  style={[
                    styles.button,
                    styles.rejectButton,
                    { backgroundColor: isDark ? 'rgba(239, 68, 68, 0.2)' : 'rgba(239, 68, 68, 0.1)' },
                  ]}
                  onPress={() => handleSubmit('reject')}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <ActivityIndicator color="#EF4444" size="small" />
                  ) : (
                    <>
                      <Ionicons name="close-circle" size={20} color="#EF4444" />
                      <ThemedText style={[styles.buttonText, { color: '#EF4444', marginLeft: 6 }]}>
                        Reject
                      </ThemedText>
                    </>
                  )}
                </TouchableOpacity>

                <TouchableOpacity
                  style={[
                    styles.button,
                    styles.acceptButton,
                    { backgroundColor: '#10B981' },
                  ]}
                  onPress={() => handleSubmit('accept')}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? (
                    <ActivityIndicator color="#FFFFFF" size="small" />
                  ) : (
                    <>
                      <Ionicons name="checkmark-circle" size={20} color="#FFFFFF" />
                      <ThemedText style={[styles.submitButtonText, { marginLeft: 6 }]}>
                        Accept
                      </ThemedText>
                    </>
                  )}
                </TouchableOpacity>
              </>
            )}
          </View>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
  },
  modalContainer: {
    borderTopLeftRadius: 24,
    borderTopRightRadius: 24,
    maxHeight: '90%',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: -4 },
    shadowOpacity: 0.15,
    shadowRadius: 12,
    elevation: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: '600',
  },
  content: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  jobCard: {
    padding: 16,
    borderRadius: 12,
    marginBottom: 20,
  },
  jobLabel: {
    fontSize: 12,
    fontWeight: '500',
    marginBottom: 4,
  },
  jobTitle: {
    fontSize: 16,
    fontWeight: '600',
  },
  sectionTitle: {
    fontSize: 15,
    fontWeight: '600',
    marginBottom: 16,
  },
  statusOption: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    borderRadius: 12,
    marginBottom: 12,
    gap: 12,
  },
  statusInfo: {
    flex: 1,
  },
  statusLabel: {
    fontSize: 16,
    fontWeight: '600',
    marginBottom: 2,
  },
  statusDescription: {
    fontSize: 13,
  },
  requestedStatus: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 20,
    borderRadius: 12,
    borderWidth: 2,
    marginBottom: 16,
    gap: 16,
  },
  requestedInfo: {
    flex: 1,
  },
  requestedLabel: {
    fontSize: 18,
    fontWeight: '600',
    marginBottom: 4,
  },
  requestedDescription: {
    fontSize: 14,
  },
  infoBox: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 12,
    borderRadius: 8,
    gap: 10,
    marginTop: 8,
  },
  infoText: {
    flex: 1,
    fontSize: 13,
    lineHeight: 18,
  },
  footer: {
    flexDirection: 'row',
    gap: 12,
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
  },
  button: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 14,
    borderRadius: 12,
    gap: 6,
  },
  cancelButton: {
    flex: 0.8,
  },
  submitButton: {
    flex: 1.2,
  },
  rejectButton: {
    flex: 1,
  },
  acceptButton: {
    flex: 1,
  },
  buttonText: {
    fontSize: 15,
    fontWeight: '600',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: 15,
    fontWeight: '600',
  },
});

export default EndContractModal;
